/**
 * PromoCodeForm
 *
 * "Have a promo code?" under the plan choice it affects: a collapsed disclosure holding the code
 * input. A valid code goes straight to checkout for the plan the code unlocks.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRedeemCouponMutation } from '../../hooks/useBilling'
import { Button } from '../ui/button'
import { Disclosure } from './Disclosure'

export function PromoCodeForm() {
  const navigate = useNavigate()
  const redeemCouponMutation = useRedeemCouponMutation()
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)

  const apply = async () => {
    if (!code.trim()) {
      setError('Please enter a coupon code')
      return
    }
    try {
      setError(null)
      const validation = await redeemCouponMutation.mutateAsync(code)
      if (validation.valid && validation.bundleTemplate) {
        navigate(`/settings/billing/checkout?bundleId=${validation.bundleTemplate.id}`, {
          state: { bundle: validation.bundleTemplate },
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid coupon code')
    }
  }

  return (
    <div data-testid="promo-code">
      <Disclosure
        id="promo-code"
        toggleTestId="coupon-toggle"
        label="Have a promo code?"
        open={open}
        onToggle={() => {
          setOpen((v) => !v)
          setError(null)
        }}
      >
        <div className="mt-1 flex flex-col gap-2">
          <div className="flex gap-2">
            <label htmlFor="coupon-input" className="sr-only">
              Promo code
            </label>
            <input
              id="coupon-input"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void apply()}
              placeholder="Enter code..."
              data-testid="coupon-input"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'coupon-error' : undefined}
              className={`min-h-[44px] flex-1 px-3 py-2 text-base border rounded-md outline-none max-w-[240px] focus:ring-2 focus:ring-primary bg-background text-foreground ${
                error ? 'border-destructive' : 'border-input'
              }`}
            />
            <Button
              variant="outline"
              onClick={() => void apply()}
              disabled={redeemCouponMutation.isPending}
              data-testid="coupon-apply"
              className="whitespace-nowrap min-h-[44px]"
            >
              {redeemCouponMutation.isPending ? 'Checking...' : 'Apply'}
            </Button>
          </div>
          {error && (
            <p
              id="coupon-error"
              data-testid="coupon-error"
              role="alert"
              className="m-0 text-sm text-destructive"
            >
              {error}
            </p>
          )}
        </div>
      </Disclosure>
    </div>
  )
}
